import "./AccountSettingsPage.scss";

import {
  Alert,
  ChevronStartIcon,
  Flex,
  Menu,
  MenuItemProps,
  ShorthandCollection,
  Text,
} from "@fluentui/react-northstar";
import { SubscriptionDTO, UserSubscriptionDTO } from "@teams/types/api";
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";

import { useUserInfo } from "../actions/useUserInfo";
import { SubscriptionUpgrade } from "../components/Checkout/SubscriptionUpgrade";
import { NavBarNew } from "../components/NavBar/NavBarNew";
import { NavigationHeader } from "../components/NavigationHeader";
import { PollyDialog } from "../components/PollyDialog";
import { PollyIcon } from "../components/PollyIcon";
import { TeamsLoader } from "../components/TeamsLoader";
import { isError } from "../services/ChannelApi";
import { subscriptionApi } from "../services/SubscriptionApi";
import { useTeamsContext } from "../state/TeamsContextProvider";
import { getPlanLabel } from "../types/subscriptionHelper";
import { Activity } from "./AccountSettings/Activity";
import { Billing } from "./AccountSettings/Billing";
import { UsersAndPermissionsForAccount } from "./AccountSettings/UsersAndPermissionsForAccount";

export const AccountSettingsPage: React.FC = () => {
  const history = useHistory();
  const { user } = useUserInfo();
  const [selectedSubscription, setSelectedSubscription] = useState<UserSubscriptionDTO>();

  const subscriptions = useMemo(() => {
    const subs = user.adminSubscriptions.length ? user.adminSubscriptions : user.subscriptions;
    setSelectedSubscription(subs[0]);
    return subs;
  }, [user]);

  if (!subscriptions.length) {
    return <Alert danger content="You do not have any active paid subscriptions!" />;
  }

  return (
    <Flex column gap="gap.medium" className="account-settings">
      <NavBarNew />

      <NavigationHeader
        breadcrumbs={[
          {
            title: "Back",
            link: "",
            icon: <ChevronStartIcon />,
            onClick: e => {
              e.preventDefault();
              history.goBack();
            },
          },
        ]}
      />

      <Flex>
        {subscriptions.length > 1 && (
          <Flex.Item>
            <Flex column vAlign="stretch">
              <Text content="Subscriptions" weight="bold" align="center" as="div" />
              <Flex.Item grow={1}>
                <div>
                  <Menu
                    vertical
                    pointing
                    defaultActiveIndex={0}
                    items={subscriptions.map<MenuItemProps & { key: string }>(sub => ({
                      key: sub.id,
                      icon: <PollyIcon icon="pay-outline-icon" />,
                      content: getPlanLabel(sub.plan, sub.planLevel, sub.scope),
                      onClick: () => setSelectedSubscription(sub),
                    }))}
                  />
                </div>
              </Flex.Item>
            </Flex>
          </Flex.Item>
        )}
        <Flex.Item grow={1}>
          <div>
            {selectedSubscription && <SubscriptionSettings subscription={selectedSubscription} />}
          </div>
        </Flex.Item>
      </Flex>
    </Flex>
  );
};

const SubscriptionSettings: React.FC<{ subscription: UserSubscriptionDTO }> = ({
  subscription,
}) => {
  const { teamsContext } = useTeamsContext();
  const [selectedPage, setSelectedPage] = useState<"billing" | "users" | "activity">("users");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userSubscription, setUserSubscription] = useState<SubscriptionDTO>();
  const [showUpgrade, setShowUpgrade] = useState<boolean>();
  const { loadUser } = useUserInfo();

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const loadSubscription = useCallback(async () => {
    setIsLoading(true);

    const sub = await subscriptionApi.getSubscription(teamsContext, subscription.id);
    setIsLoading(false);

    if (isError(sub)) {
      console.error(sub);
      return;
    }

    setUserSubscription(sub);
  }, [teamsContext, subscription]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const menuItems: ShorthandCollection<MenuItemProps> = useMemo(() => {
    return [
      (subscription.isOwner || subscription.isAdmin) &&
        subscription.provider === "stripe" && {
          key: "billing",
          content: <Text content="Billing and Plans" />,
          onClick: () => setSelectedPage("billing"),
        },
      {
        key: "users",
        content: <Text content="Users and License Management" />,
        onClick: () => setSelectedPage("users"),
      },
      (subscription.isOwner || subscription.isAdmin) && {
        key: "activity",
        content: <Text content="Usage Activity" />,
        onClick: () => setSelectedPage("activity"),
      },
    ];
  }, [subscription.isAdmin, subscription.isOwner, subscription.provider]);

  useEffect(() => {
    const firstItem = menuItems.filter(item => !!item)[0];
    if (firstItem) {
      setSelectedPage((firstItem as ReactElement).key as "billing" | "users" | "activity");
    }
  }, [menuItems]);

  return (
    <TeamsLoader isLoading={isLoading} loadingMessage="Loading...">
      <div>
        <Menu
          defaultActiveIndex={
            menuItems.findIndex(item => {
              const menuItem = item as { key: "billing" | "users" | "activity" };
              return typeof menuItem === "object" && menuItem?.key === selectedPage;
            }) ?? 0
          }
          primary
          items={menuItems}
          underlined
          styles={{ borderBottom: 0 }}
          className="account-settings__menu"
        />

        <div className="account-settings__menu-content">
          {selectedPage === "billing" && (subscription.isOwner || subscription.isAdmin) && (
            <Billing subscription={subscription} onUpgradeRequest={() => setShowUpgrade(true)} />
          )}
          {selectedPage === "activity" && (subscription.isOwner || subscription.isAdmin) && (
            <Activity subscription={subscription} onUpgradeRequest={() => setShowUpgrade(true)} />
          )}
          {selectedPage === "users" && userSubscription && (
            <UsersAndPermissionsForAccount
              canOwn={subscription.isOwner || subscription.isAdmin}
              owners={userSubscription.owners}
              showUsers={userSubscription.scope === "user"}
              allowAddUsers={userSubscription.scope === "user"}
              maxUsers={userSubscription.planQuantity}
              users={userSubscription.users}
              onUpgradeRequest={() => setShowUpgrade(true)}
              onAddOwner={async user => {
                const result = await subscriptionApi.addOwner(teamsContext, subscription.id, {
                  aadObjectId: user.aadObjectId,
                  name: user.name,
                  upn: user.userPrincipalName,
                  teamsUserId: user.id,
                });

                if (!isError(result)) {
                  await loadSubscription();
                }

                return result;
              }}
              onRemoveOwner={async owner => {
                const result = await subscriptionApi.removeOwner(
                  teamsContext,
                  subscription.id,
                  owner.aadObjectId
                );
                if (!isError(result)) {
                  const promises: Promise<any>[] = [];

                  promises.push(loadSubscription());

                  if (owner.aadObjectId === teamsContext?.user?.id) {
                    promises.push(loadUser());
                  }
                  await Promise.resolve(promises);
                }
                return result;
              }}
              onAddUser={async user => {
                const result = await subscriptionApi.addUser(teamsContext, subscription.id, {
                  aadObjectId: user.aadObjectId,
                  name: user.name,
                  upn: user.userPrincipalName,
                  teamsUserId: user.id,
                });

                if (!isError(result)) {
                  await loadSubscription();
                }

                return result;
              }}
              onRemoveUser={async user => {
                const result = await subscriptionApi.removeUser(
                  teamsContext,
                  subscription.id,
                  user.aadObjectId
                );
                if (!isError(result)) {
                  await loadSubscription();
                }
                return result;
              }}
            />
          )}
        </div>
      </div>

      {showUpgrade && (
        <PollyDialog
          open
          header="Upgrade Plan"
          onCancel={() => setShowUpgrade(false)}
          width="90%"
          maxWidth="1000px"
        >
          <SubscriptionUpgrade
            onUpgraded={async () => {
              setShowUpgrade(false);
              await loadUser(true);
              await loadSubscription();
            }}
          />
        </PollyDialog>
      )}
    </TeamsLoader>
  );
};
